import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Eye, ShieldCheck, XCircle, LogOut, Home, Plus, Ban } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { LogoMarkStatic } from "@/components/LogoMark";

// BLOCK 167 ,  seller status dashboard. Row-level scoping is enforced
// server-side (GET /api/seller/listings filters by the authenticated
// account's own verified email) ,  this page never passes its own filter.
const STATUS_STYLES = {
  "Under Review": {
    icon: Clock,
    className: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  },
  "Live & Visible to Buyers": {
    icon: Eye,
    className: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  },
  "Approved ,  Reserved for a Specific Buyer": {
    icon: ShieldCheck,
    className: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  },
  "Not Accepted": {
    icon: XCircle,
    className: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  },
  "Withdrawn by you": {
    icon: Ban,
    className: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  },
};

function StatusBadge({ label }) {
  const cfg = STATUS_STYLES[label] || STATUS_STYLES["Under Review"];
  const Icon = cfg.icon;
  return (
    <span
      data-testid="seller-listing-status-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border ${cfg.className}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

export default function SellerDashboard() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [withdrawing, setWithdrawing] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/seller/listings")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const withdraw = async (oid) => {
    if (!window.confirm("Withdraw this listing? It will no longer be visible to buyers. This cannot be undone by you.")) return;
    setWithdrawing(oid);
    try {
      await api.post(`/seller/listings/${oid}/withdraw`);
      toast.success("Listing withdrawn.");
      load();
    } catch {
      toast.error("Could not withdraw this listing. Try again.");
    } finally {
      setWithdrawing(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100">
      <div className="glass-nav sticky top-0 z-30 px-6 md:px-10 py-4 flex items-center justify-between border-b border-white/8">
        <Link to="/" data-testid="seller-home-link">
          <LogoMarkStatic className="h-10" />
        </Link>
        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <div className="text-sm truncate" data-testid="seller-account-name">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Seller Account</div>
          </div>
          <button
            onClick={logout}
            data-testid="seller-logout-btn"
            className="p-2 rounded-sm text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
            title="Logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">My Listings</div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Track your submissions.</h1>
            <p className="text-sm text-slate-400 mt-2">
              Every listing you've submitted to Acquigine, and where it stands right now.
            </p>
          </div>
          <a href="/sell" target="_blank" rel="noopener noreferrer" data-testid="seller-submit-another-link" className="btn-ghost text-sm inline-flex items-center gap-2 shrink-0">
            <Plus className="h-4 w-4" /> Submit another listing
          </a>
        </div>


        <div className="mt-8 space-y-3" data-testid="seller-listings-list">
          {loading && <div className="text-slate-500 text-sm">Loading your listings…</div>}
          {!loading && error && (
            <div className="text-rose-400 text-sm" data-testid="seller-listings-error">
              Couldn't load your listings. Try refreshing the page.
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="ae-card p-8 text-center" data-testid="seller-listings-empty">
              <Home className="h-8 w-8 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No listings found for this account yet.</p>
            </div>
          )}
          {items.map((it) => (
            <div key={it.oid} className="ae-card p-5 flex items-start justify-between gap-4" data-testid={`seller-listing-${it.oid}`}>
              <div className="min-w-0">
                <div className="font-heading text-lg truncate">{it.title}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {it.city}, {it.state} · {it.asset_type} · {fmtCurrency(it.asking_price)}
                </div>
                {it.submission_status === "rejected" && it.rejection_reason && (
                  <div className="text-xs text-rose-400/80 mt-2" data-testid={`seller-listing-${it.oid}-reason`}>
                    Reason: {it.rejection_reason}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <StatusBadge label={it.status_label} />
                {!it.withdrawn && it.submission_status !== "rejected" && (
                  <button
                    onClick={() => withdraw(it.oid)}
                    disabled={withdrawing === it.oid}
                    data-testid={`seller-withdraw-btn-${it.oid}`}
                    className="text-[11px] text-slate-500 hover:text-rose-300 underline-offset-2 hover:underline disabled:opacity-50"
                  >
                    {withdrawing === it.oid ? "Withdrawing…" : "Withdraw listing"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
